import { z } from 'zod';

import { PRESET_NAMES } from './presets.js';
import {
  applyPreset,
  DEFAULT_SETTINGS,
  SETTINGS_KEY,
  SettingsSchema,
  type Settings,
} from './schema.js';

/** The earlier blobs' keys and the fields they carried that still mean the same. */
const V1_KEY = 'steelwake.settings.v1';
const V1Schema = z.object({ version: z.literal(1), preset: z.enum(PRESET_NAMES) }).loose();
const V2_KEY = 'steelwake.settings.v2';
const V2Schema = z.object({ version: z.literal(2), preset: z.enum(PRESET_NAMES) }).loose();

/** Version 2 blobs keep everything; the audio and accessibility blocks take their defaults. */
function migrateV2(raw: string): Settings | null {
  const v2 = V2Schema.safeParse(JSON.parse(raw) as unknown);
  if (!v2.success) return null;
  const { version: _version, ...carried } = v2.data;
  const parsed = SettingsSchema.safeParse({
    ...DEFAULT_SETTINGS,
    ...carried,
    version: DEFAULT_SETTINGS.version,
  });
  return parsed.success ? parsed.data : null;
}

/**
 * Version 1 blobs keep the pilot's own choices (preset, caps, FOV, sensitivity, readouts) and
 * re-derive the quality fields from the preset, which is how depth of field joins the Medium,
 * High and Ultra presets without a reset (Phase 6).
 */
function migrateV1(raw: string): Settings | null {
  const v1 = V1Schema.safeParse(JSON.parse(raw) as unknown);
  if (!v1.success) return null;
  const { version: _version, effects: _effects, ...carried } = v1.data;
  const candidate = applyPreset(
    { ...DEFAULT_SETTINGS, ...carried, version: DEFAULT_SETTINGS.version },
    v1.data.preset,
  );
  const parsed = SettingsSchema.safeParse(candidate);
  return parsed.success ? parsed.data : null;
}

export interface SettingsStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** Reads settings; anything missing, corrupt, or from another version yields the defaults. */
export function loadSettings(storage: SettingsStorage | null): Settings {
  if (!storage) return DEFAULT_SETTINGS;
  try {
    const raw = storage.getItem(SETTINGS_KEY);
    if (raw === null) {
      const v2 = storage.getItem(V2_KEY);
      if (v2 !== null) return migrateV2(v2) ?? DEFAULT_SETTINGS;
      const v1 = storage.getItem(V1_KEY);
      return (v1 !== null && migrateV1(v1)) || DEFAULT_SETTINGS;
    }
    const parsed = SettingsSchema.safeParse(JSON.parse(raw) as unknown);
    return parsed.success ? parsed.data : DEFAULT_SETTINGS;
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export function saveSettings(storage: SettingsStorage | null, settings: Settings): void {
  if (!storage) return;
  try {
    storage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // Private mode or a full quota: the in-memory settings still apply for this session.
  }
}

/** `localStorage` when the browser allows it (it can throw when storage is disabled). */
export function browserStorage(): SettingsStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}
